"use client";

import { useState, useEffect, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { Eye, EyeOff } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useCommand } from "@/hooks/production";

function LoginForm() {
  const searchParams = useSearchParams();
  const { login, isLoading, error: loginError } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // const { list } = useCommand({
  //   searchRequest: {},
  // });

  // Show toast error when login error occurs
  useEffect(() => {
    if (loginError) {
      const errorMessage =
        loginError?.message ||
        (loginError as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ||
        "로그인에 실패했습니다. 자격 증명을 확인하고 다시 시도해주세요.";
      toast.error(errorMessage);
    }
  }, [loginError]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password || !tenantId) {
      toast.warning("사용자 이름, 비밀번호 및 회사 코드를 입력해 주세요.");
      return;
    }

    // Get redirect parameter from URL
    const redirectTo = searchParams.get("redirect") || "/";

    // Call login from useAuth hook
    login({ username, password, tenantId }, redirectTo);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4 py-12 dark:bg-gray-950">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <h1 className="text-3xl font-bold text-brand-600 dark:text-brand-400">
            POP
          </h1>
        </div>

        {/* Title and Subtitle */}
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            계정에 로그인하세요
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            다시 오신 것을 환영합니다! 정보를 입력해 주세요.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Company Code */}
          <div className="space-y-2">
            <Label
              htmlFor="tenantId"
              className="text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              회사코드
            </Label>
            <Input
              id="tenantId"
              type="text"
              placeholder="회사코드를 입력하세요"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={isLoading}
              required
              className="h-11 focus-visible:outline-none focus-visible:border-purple-800 focus-visible:ring-purple-800/50 focus-visible:ring-2"
            />
          </div>

          {/* ID */}
          <div className="space-y-2">
            <Label
              htmlFor="username"
              className="text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              아이디
            </Label>
            <Input
              id="username"
              type="text"
              placeholder="Enter your ID"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
              required
              className="h-11 focus-visible:outline-none focus-visible:border-purple-800 focus-visible:ring-purple-800/50 focus-visible:ring-2"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              비밀번호
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
                className="h-11 pr-10 focus-visible:outline-none focus-visible:border-purple-800 focus-visible:ring-purple-800/50 focus-visible:ring-2"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Remember Me and Forgot Password */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
                className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600 data-[state=checked]:text-white"
              />
              <Label
                htmlFor="remember"
                className="cursor-pointer text-sm text-gray-700 dark:text-gray-300"
              >
                30일간 기억하세요
              </Label>
            </div>
            {/* <a
              href="#"
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              onClick={(e) => {
                e.preventDefault();
                // TODO: Implement forgot password functionality
              }}
            >
              Forgot Password
            </a> */}
          </div>

          {/* Sign In Button */}
          <Button
            type="submit"
            className="h-11 w-full rounded-md bg-brand-600 text-white transition hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-400"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner className="mr-2" />
                로그인 중...
              </>
            ) : (
              "로그인"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-950">
          <div className="space-y-4 text-center">
            <Spinner className="mx-auto size-8 text-purple-700 dark:text-purple-400" />
          </div>
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
